import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Paper, Typography, FormControl, InputLabel, Select,
    MenuItem, TextField, Stack, Button, useMediaQuery
} from '@mui/material';
import {
    LocalizationProvider,
    DatePicker,
} from '@mui/x-date-pickers';
import { koKR } from '@mui/x-date-pickers/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import api from '../../../../api/axios';
import MessageLogTable from './MessageLogTable';

dayjs.locale('ko');

export default function ChatLogPage() {
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState('');
    const [keyword, setKeyword] = useState('');
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());
    const isMobile = useMediaQuery('(max-width:600px)');

    useEffect(() => {
        api.get('/api/chat/categories/')
            .then(res => setCategories(res.data.categories || []))
            .catch(() => alert('카테고리 목록을 불러오지 못했습니다.'));
    }, []);

    const selectedName = useMemo(() => {
        if (selected === 'all') return '';
        return categories.find(c => c.id === selected)?.name || '';
    }, [selected, categories]);

    const triggerExcelDownload = () =>
        window.dispatchEvent(new Event('exportToExcel'));

    // ✅ 모바일은 "하얀 Paper 박스" 제거: Box로 대체
    const FilterWrap = isMobile ? Box : Paper;
    const filterWrapProps = isMobile ? {} : { variant: 'outlined' };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ko"
            localeText={koKR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Box
                sx={{
                    p: isMobile ? 1.5 : 3,     // ✅ 모바일 패딩도 살짝 줄임
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'hidden',
                }}
            >
                <FilterWrap
                    {...filterWrapProps}
                    sx={{
                        // ✅ 모바일이면 "칸" 느낌 제거 (테두리/배경/패딩 없음)
                        p: isMobile ? 0 : 2,
                        mb: 3,
                        borderRadius: 2,
                        width: '100%',
                        minWidth: 0,
                        ...(isMobile ? { bgcolor: 'transparent', border: 0, boxShadow: 'none' } : {}),
                    }}
                >
                    {isMobile ? (
                        // 모바일 레이아웃
                        <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                            <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                                <InputLabel>채팅방</InputLabel>
                                <Select
                                    value={selected}
                                    label="채팅방"
                                    onChange={e => setSelected(e.target.value)}
                                    sx={{ minWidth: 0 }}
                                >
                                    <MenuItem value="all">모든카테고리</MenuItem>
                                    {categories.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.emoji} {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <DatePicker
                                label="시작일"
                                value={startDate}
                                onChange={v => setStartDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: { size: 'small', fullWidth: true },
                                }}
                            />

                            <DatePicker
                                label="종료일"
                                value={endDate}
                                onChange={v => setEndDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: { size: 'small', fullWidth: true },
                                }}
                            />

                            <TextField
                                label="닉네임/메시지"
                                size="small"
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                                fullWidth
                            />

                            <Button
                                variant="outlined"
                                size="medium"
                                disabled={!selected}
                                onClick={triggerExcelDownload}
                                fullWidth
                                sx={{ height: 40 }}
                            >
                                엑셀로 저장
                            </Button>
                        </Stack>
                    ) : (
                        // PC 레이아웃
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            flexWrap="nowrap"
                            sx={{ width: '100%', minWidth: 0 }}
                        >
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>채팅방</InputLabel>
                                <Select
                                    value={selected}
                                    label="채팅방"
                                    onChange={e => setSelected(e.target.value)}
                                >
                                    <MenuItem value="all">모든카테고리</MenuItem>
                                    {categories.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.emoji} {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <DatePicker
                                label="시작일"
                                value={startDate}
                                onChange={v => setStartDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: { size: 'small', sx: { minWidth: 140 } },
                                }}
                            />

                            <DatePicker
                                label="종료일"
                                value={endDate}
                                onChange={v => setEndDate(v)}
                                format="YYYY-MM-DD"
                                slotProps={{
                                    textField: { size: 'small', sx: { minWidth: 140 } },
                                }}
                            />

                            <TextField
                                label="닉네임/메시지"
                                size="small"
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                                sx={{
                                    minWidth: 280,      // ✅ PC에서 검색창 너무 작아지는 현상 방지
                                    flexGrow: 1,
                                }}
                            />

                            <Button
                                variant="outlined"
                                size="medium"
                                disabled={!selected}
                                onClick={triggerExcelDownload}
                                sx={{
                                    height: 40,
                                    whiteSpace: 'nowrap',
                                    width: 'auto',
                                    minWidth: 120,
                                    flex: 'none',
                                }}
                            >
                                엑셀로 저장
                            </Button>
                        </Stack>
                    )}
                </FilterWrap>

                {selected ? (
                    <MessageLogTable
                        categoryId={selected}
                        categoryName={selectedName}
                        keyword={keyword}
                        startDate={startDate.format('YYYY-MM-DD')}
                        endDate={endDate.format('YYYY-MM-DD')}
                    />
                ) : (
                    <Typography color="text.secondary">
                        채팅방을 선택하면 메시지 로그가 표시됩니다.
                    </Typography>
                )}
            </Box>
        </LocalizationProvider>
    );
}
